import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import api from '@/lib/api';
import Preloader from '@/components/Preloader';
import { reais, type Composicao } from './tipos';

/**
 * A conta de uma linha da planilha, setor a setor, como o cliente faz à mão:
 * "001 Salário normal 6.294,36 + 452 Bônus 337,92 = 6.632,28".
 */
export default function ComposicaoDaLinha({ folhaId, lancamentoId }: { folhaId: number; lancamentoId: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['folha-lancamentos', folhaId, 'composicao', lancamentoId],
    queryFn: async () =>
      (await api.get<{ data: Composicao }>(`/folhas/${folhaId}/lancamentos/${lancamentoId}/composicao`)).data.data,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-sm py-sm text-[13px] text-muted">
        <Preloader />
        Montando a conta…
      </div>
    );
  }

  if (isError || !data) {
    return <p className="py-sm text-[13px] text-danger">Não foi possível montar a conta desta linha.</p>;
  }

  const comValor = data.setores.filter((setor) => setor.itens.length > 0);
  const divergente = Math.abs(data.total - data.valor) >= 0.005;

  return (
    <div className="py-sm text-[13px]">
      <p className="text-muted">{data.regra}</p>

      {data.setores.length > 0 && comValor.length === 0 && (
        <p className="mt-xs text-muted">Nenhum valor nesta competência: a linha vai zerada.</p>
      )}

      {comValor.map((setor) => (
        <div key={setor.codigo} className="mt-sm max-w-[560px]">
          <p className="text-[12px] font-bold text-ink-soft">
            <span className="tabular-nums">{setor.codigo}</span> {setor.nome}
          </p>
          <table className="w-full">
            <tbody>
              {setor.itens.map((item) => (
                <tr key={item.rotulo}>
                  <td className="py-[1px] pr-md text-ink">{item.rotulo}</td>
                  <td className={`py-[1px] text-right tabular-nums ${item.valor < 0 ? 'text-danger' : 'text-ink'}`}>
                    {reais.format(item.valor)}
                  </td>
                </tr>
              ))}
              {(setor.itens.length > 1 || comValor.length > 1) && (
                <tr className="border-t border-border">
                  <td className="py-[1px] pr-md text-muted">{comValor.length > 1 ? `Total de ${setor.nome}` : 'Total'}</td>
                  <td className="py-[1px] text-right font-bold text-ink tabular-nums">{reais.format(setor.total)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {divergente && (
        <p className="mt-sm flex items-center gap-xs text-warning">
          <TriangleAlert size={14} />
          Com os cadastros de hoje a conta dá {reais.format(data.total)}, mas a linha foi gravada com {reais.format(data.valor)}: a
          regra mudou depois que a competência foi enviada ao ERP.
        </p>
      )}
    </div>
  );
}
